import { cn } from '@gozoki/ui/lib/utils'
import { useEffect, useRef, useState } from 'react'
import { type VideoRTCStateDetail, VideoRTC } from './video-rtc'

if (typeof window !== 'undefined' && !customElements.get('video-rtc')) {
  customElements.define('video-rtc', VideoRTC)
}

type PlayerStatus = 'connecting' | 'connected' | 'error'

interface Session {
  baseUrl: string
  token: string
}

const buildStreamUrl = (cameraName: string, session: Session): string => {
  const url = new URL('/api/ws', session.baseUrl)
  url.searchParams.set('src', cameraName)
  url.searchParams.set('token', session.token)
  return url.toString()
}

/**
 * Player go2rtc (WebRTC + repli MSE), porté du back-office (`store-camera-player.tsx`, utilisé par
 * les vues de `/cameras/new`) sans dépendances UI externes. Plusieurs instances jouent en même
 * temps : une par vignette du widget, une par tuile de la modale.
 *
 * La vidéo remplit la tuile (`object-cover`) : pas de bandes noires autour d'un flux dont le ratio
 * diffère de celui de la tuile.
 */
export function StoreCameraPlayer({
  cameraName,
  session,
  className,
}: {
  cameraName: string
  session: Session
  className?: string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<VideoRTC | null>(null)
  const [status, setStatus] = useState<PlayerStatus>('connecting')

  const sessionRef = useRef(session)
  useEffect(() => {
    sessionRef.current = session
  }, [session])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    setStatus('connecting')

    const player = document.createElement('video-rtc') as VideoRTC
    player.filterPrivateCandidates = true
    player.resolveHostnameCandidates = true
    player.mode = 'webrtc,mse'

    const handleVideoPlaying = () => setStatus('connected')

    const handleStateChange = (event: Event) => {
      const { detail } = event as CustomEvent<VideoRTCStateDetail>
      if (detail.status === 'connected') {
        setStatus('connected')
      } else if (detail.status === 'error') {
        const video = player.video
        const fallbackActive =
          !!video &&
          (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ||
            video.networkState === HTMLMediaElement.NETWORK_LOADING)
        if (fallbackActive) return
        setStatus('error')
      }
    }

    player.addEventListener('videortcstatechange', handleStateChange)
    container.appendChild(player)
    player.style.cssText = 'display:block;width:100%;height:100%;'

    if (player.video) {
      player.video.controls = false
      player.video.muted = true
      player.video.className = 'w-full h-full object-cover'
      player.video.addEventListener('playing', handleVideoPlaying)
    }

    player.src = buildStreamUrl(cameraName, sessionRef.current)
    playerRef.current = player

    return () => {
      playerRef.current = null
      player.removeEventListener('videortcstatechange', handleStateChange)
      player.video?.removeEventListener('playing', handleVideoPlaying)
      player.dispose()
      if (container.contains(player)) {
        container.removeChild(player)
      }
    }
  }, [cameraName])

  // Le kiosk reste allumé des heures : `video-rtc` se reconnecte seul après une coupure, mais avec
  // l'URL qu'on lui a donnée. On y reporte chaque nouveau jeton de session (rafraîchi avant son
  // expiration) pour qu'une reconnexion tardive ne parte pas avec un jeton périmé. Sur un flux
  // établi, réaffecter `src` ne relance rien (`onconnect` sort tant que la connexion existe).
  useEffect(() => {
    if (playerRef.current) playerRef.current.src = buildStreamUrl(cameraName, session)
  }, [cameraName, session])

  return (
    <div className={cn('relative overflow-hidden bg-black', className)}>
      <div ref={containerRef} className="absolute inset-0" />
      {status !== 'connected' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-center font-sans text-[14px] text-white">
          {status === 'connecting' ? (
            <>
              <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Connexion…
            </>
          ) : (
            <span>Flux indisponible</span>
          )}
        </div>
      )}
    </div>
  )
}
